import BottomSheet from "@components/BottomSheet/BottomSheet"
import BottomSheetHeader from "@components/BottomSheetHeader/BottomSheetHeader"
import AlertBox from "@components/AlertBox/AlertBox"
import {
  Container,
  TopUpCryptoSelectAssetContainer,
  Divider,
  NetworksContainer,
  NetworkItem,
  NetworkName,
  NetworkItemWrapper,
  NetworkDivider,
  ButtonContainer
} from "@pages/TopUpCryptoSelectAsset/TopUpCryptoSelectAsset.styles"
import TopUpOptionList from "@components/TopUpOptionList/TopUpOptionList"
import { BLACK, GRAYSCALE_300, PALE_BLUE, WHITE } from "@styles/colors"
import { GIVONIC } from "@styles/fonts"
import { useState, useMemo, useEffect } from "react"
import { useNavigate } from "react-router"
import Header from "@components/Header/Header"
import PrimaryButton from "@components/PrimaryButton/PrimaryButton"
import { filterAndExtract, groupArrayBy } from "@lib/utils"
import RadioButton from "@components/RadioButton/RadioButton"
import { useTranslation } from "react-i18next"
import useSupportedCryptoCurrency from "@hooks/useSupportedCryptoCurrency"
import LoadingModal from "@components/LoadingModal/LoadingModal"
import useCryptoDepositAddress from "@hooks/useCryptoDepositAddress"
import Snackbar from "@components/Snackbar/Snackbar"
import ReactDOM from "react-dom"
import useExchangeRate from "@hooks/useExchangeRate"
import { setHeaderColor } from "@lib/telegram"

type Option = {
  iconUrl: string
  name?: string
  boldName?: string
  grayName?: string
  selectedValue?: string
}

const TopUpCryptoSelectAsset = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    getDepositAddress,
    isLoading: isLoadingDepositAddress,
    error: errorDepositAddress
  } = useCryptoDepositAddress()
  const {
    getExchangeRate,
    isLoading: isLoadingExchangeRate,
    error: errorExchangeRate
  } = useExchangeRate()
  const { data: supportedCryptoCurrencies, isLoading } = useSupportedCryptoCurrency()
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const [selectedNetwork, setSelectedNetwork] = useState<string>('')
  const [selectedAssetGroup, setSelectedAssetGroup] = useState<string>('')
  const [windowHeight, setWindowHeight] = useState(window.innerHeight || 800)
  const [errorCounter, setErrorCounter] = useState(0)

  // Update window height on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Calculate dynamic height based on number of networks
  const bottomSheetHeight = useMemo(() => {
    // Base height for alert + divider + button + padding
    const baseHeight = 270
    // Height per network item (including divider)
    const networkItemHeight = 50
    // Calculate total height needed
    const totalHeight = baseHeight + (
      supportedCryptoCurrencies
        ? filterAndExtract(supportedCryptoCurrencies, 'assetGroup', selectedAssetGroup, 'network').length * networkItemHeight
        : 0
    )
    // Convert to percentage of viewport height

    return `${totalHeight}px`
  }, [
    supportedCryptoCurrencies,
    selectedAssetGroup,
    windowHeight
  ])

  // Transform supportedCryptoCurrencies into options for TopUpOptionList
  const options = useMemo(() => {
    if (!supportedCryptoCurrencies) return []

    return groupArrayBy(supportedCryptoCurrencies, 'assetGroup').map(item => ({
      iconUrl: item.iconUrl,
      name: item.assetGroup,
      grayName: item.priceTicker.toUpperCase()
    }))
  }, [supportedCryptoCurrencies])

  const handleBackClick = () => {
    navigate('/top-up-method')
  }

  const handleOpenBottomSheet = (option: Option) => {
    setSelectedAssetGroup(option.name || '')
    setIsBottomSheetOpen(true)
  }

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false)
    setSelectedAssetGroup('')
    setSelectedNetwork('')
  }

  const handleNetworkChange = (network: string) => {
    setSelectedNetwork(network)
  }

  const handleGetDeposit = async () => {
    try {
      const asset = supportedCryptoCurrencies?.find(item => item.assetGroup === selectedAssetGroup && item.network === selectedNetwork)
      const depositAddress = await getDepositAddress(asset?.id || '')
      const exchangeRate = await getExchangeRate(asset?.id || '')
      if (asset && depositAddress && exchangeRate) {
        navigate('/top-up-crypto-deposit-address', {
          state: {
            id: asset.id,
            cryptoName: selectedAssetGroup,
            network: selectedNetwork,
            iconUrl: options.find(option => option.name === selectedAssetGroup)?.iconUrl,
            depositAddress: depositAddress,
            rate: exchangeRate,
            ticker: asset.priceTicker,
            decimal: asset.uiDecimals
          }
        })
      }
    } catch (error) {
      setErrorCounter(prev => prev + 1)
    }
  }

  useEffect(() => {
    setHeaderColor(WHITE)
  }, [])

  return (
    <Container>
      <Header title={t('topUpCrypto.selectAsset.header')} onBackClick={handleBackClick} showBackButton />
      <TopUpCryptoSelectAssetContainer>
        <div>
          {isLoading ? (
            <LoadingModal visible={isLoading} />
          ) : (
            <TopUpOptionList
              options={options}
              title={t('topUpCrypto.selectAsset.cryptocurrencies')}
              onClick={handleOpenBottomSheet}
              selectedValue={selectedAssetGroup}
            />
          )}
          <BottomSheet
            height={bottomSheetHeight}
            isOpen={isBottomSheetOpen}
            onClose={handleCloseBottomSheet}
            header={
              <BottomSheetHeader
                title={t('topUpCrypto.selectAsset.bottomSheet.title')}
              />
            }
          >
            <div style={{ height: '100%' }}>
              <AlertBox
                alertText={t('topUpCrypto.selectAsset.bottomSheet.alertText')}
                backgroundColor={PALE_BLUE}
                borderColor={GRAYSCALE_300}
                iconExclamationColor={BLACK}
                iconOutlineColor={BLACK}
                iconFillColor="transparent"
                fontFamily={GIVONIC.regular}
              />
              <Divider />
              <NetworksContainer>
                {supportedCryptoCurrencies && filterAndExtract(supportedCryptoCurrencies, 'assetGroup', selectedAssetGroup, 'network').map((network, index, networks) => (
                  <NetworkItemWrapper key={index}>
                    <NetworkItem
                      onClick={() => handleNetworkChange(String(network))}
                    >
                      <NetworkName>{network}</NetworkName>
                      <RadioButton
                        name="network"
                        value={String(network)}
                        checked={selectedNetwork === String(network)}
                        onChange={() => handleNetworkChange(String(network))}
                        outlineColor="#550372"
                        outlineWidth={1.5}
                        centerColor="#550372"
                        size={20}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                    </NetworkItem>
                    {index !== networks.length - 1 && <NetworkDivider />}
                  </NetworkItemWrapper>
                ))}
              </NetworksContainer>
            </div>
            <ButtonContainer>
              <PrimaryButton
                disabled={!selectedNetwork}
                onClick={handleGetDeposit}
              >{t('topUpCrypto.selectAsset.bottomSheet.getDepositAddressButton')}</PrimaryButton>
            </ButtonContainer>
          </BottomSheet>
        </div>
      </TopUpCryptoSelectAssetContainer>
      {ReactDOM.createPortal(
        <div style={{ position: 'absolute', zIndex: 10000 }}>
          <LoadingModal visible={isLoadingDepositAddress || isLoadingExchangeRate} />
        </div>
        , document.body)}
      <Snackbar
        key={`error-${errorCounter}`}
        message={errorDepositAddress || errorExchangeRate || ''}
        isVisible={!!errorDepositAddress || !!errorExchangeRate}
        duration={5000}
        width="90%"
      />
    </Container>
  )
}

export default TopUpCryptoSelectAsset
